import { ar } from './ar';
import { en } from './en';
import type { Locale, MessageKey, Messages, Plural } from './types';
import { LOCALE_DIR } from './types';

export const CATALOGUES: Record<Locale, Messages> = { en, ar };

/**
 * The BCP-47 tag each locale formats with. Arabic pins the Arabic-Indic
 * numbering system: ICU's default for `ar` differs between Node builds and
 * browsers, and a child should not see different digits depending on which
 * device they open the app on. Switch `ar` to plain 'ar' (or '-u-nu-latn') if
 * Latin digits turn out to be the house preference — it is this one line.
 */
const FORMAT_TAG: Record<Locale, string> = {
  en: 'en',
  ar: 'ar-u-nu-arab',
};

/**
 * CLDR's stand-in for a month a build has no name for: `M03` rather than
 * `Rabīʿ al-Awwal`. A stripped-down ICU — some Android WebViews ship one —
 * resolves the Islamic calendar and then renders its months like this, which a
 * reader takes for a Gregorian month number: `M03 25 – M04 2` is a correct
 * Hijri date that says "the end of March" to everyone who looks at it.
 */
const PLACEHOLDER_MONTH = /\bM\d{2}\b/;

export type Params = Record<string, string | number>;

function interpolate(template: string, params: Params | undefined): string {
  if (!params) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  );
}

function isPlural(value: string | Plural): value is Plural {
  return typeof value !== 'string';
}

export interface Translator {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  t: (key: MessageKey, params?: Params) => string;
  /** Locale-aware digits: Arabic renders ١٢ rather than 12. */
  number: (value: number) => string;
  /** Locale-aware clock, so `ar` gets ٥:١٢ ص rather than 5:12 AM. */
  time: (hhmm: string) => string;
  /** The narrow weekday and the day of the month, for the report's grid. */
  weekday: (date: Date) => string;
  dayOfMonth: (date: Date) => string;
  /**
   * A whole day, named. The two above are cut down to fit a grid column and
   * say nothing on their own — "M" and "3" is not a day anyone can be told
   * they are recording against.
   */
  dayLabel: (date: Date) => string;
  hijri: (date?: Date) => string;
  /**
   * A date range on the Hijri calendar, collapsing a shared month — or null
   * where the device cannot actually render one. A caller with a Gregorian
   * range of its own should draw nothing rather than the same days twice.
   */
  hijriRange: (from: Date, to: Date) => string | null;
  /** The same range on the calendar the report's day numbers are counted on. */
  gregorianRange: (from: Date, to: Date) => string;
}

export function createTranslator(locale: Locale): Translator {
  const messages = CATALOGUES[locale];
  const tag = FORMAT_TAG[locale];
  const plurals = new Intl.PluralRules(locale);
  const numbers = new Intl.NumberFormat(tag);

  function t(key: MessageKey, params?: Params): string {
    const entry = messages[key];
    if (!isPlural(entry)) return interpolate(entry, params);

    const count = typeof params?.count === 'number' ? params.count : 0;
    const category = plurals.select(count);
    // `other` is required by the type, so this can never fall through.
    const template = entry[category] ?? entry.other;
    return interpolate(template, { ...params, count: numbers.format(count) });
  }

  const hijriTag = tag.includes('-u-')
    ? `${tag}-ca-islamic-umalqura`
    : `${tag}-u-ca-islamic-umalqura`;

  /**
   * A range carries its year; a single date does not.
   *
   * Today needs no year — it is today. A range does: the report's pager steps
   * back a week at a time with nothing to stop it, and every Hijri month name
   * comes round again about every three hundred and fifty-four days.
   */
  const DAY_AND_MONTH: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long' };
  const WHOLE_DATE: Intl.DateTimeFormatOptions = { ...DAY_AND_MONTH, year: 'numeric' };

  /**
   * A formatter that is really on the Hijri calendar, or nothing.
   *
   * Intl does NOT complain about a calendar it cannot supply: an unknown or
   * unsupported `ca` resolves to the Gregorian one and formats happily, so the
   * catch here only ever fires on a malformed tag. Asking what actually
   * resolved is the only way to know a Hijri date is what came back.
   */
  function hijriFormat(options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat | null {
    try {
      const format = new Intl.DateTimeFormat(hijriTag, options);
      return format.resolvedOptions().calendar.startsWith('islamic') ? format : null;
    } catch {
      return null;
    }
  }

  /** Rendered, unless what came back cannot be read as a Hijri date. */
  function hijriText(
    options: Intl.DateTimeFormatOptions,
    render: (format: Intl.DateTimeFormat) => string,
  ): string | null {
    const format = hijriFormat(options);
    if (!format) return null;
    const rendered = render(format);
    return PLACEHOLDER_MONTH.test(rendered) ? null : rendered;
  }

  return {
    locale,
    dir: LOCALE_DIR[locale],
    t,
    number: (value) => numbers.format(value),
    time: (hhmm) => {
      const [h, m] = hhmm.split(':').map(Number);
      const at = new Date(2000, 0, 1, h ?? 0, m ?? 0);
      return new Intl.DateTimeFormat(tag, { hour: 'numeric', minute: '2-digit' }).format(at);
    },
    // `en` needs a -u- to start its extension sequence; `ar-u-nu-arab` already
    // has one and must continue it. Getting this wrong makes the tag invalid,
    // and Intl falls back to the Gregorian calendar without complaining.
    // Both go through the pinned tag, not the bare locale: `ar` alone gives
    // Latin digits for the day of the month, which is the ICU default this
    // whole layer exists to override.
    weekday: (date) => new Intl.DateTimeFormat(tag, { weekday: 'narrow' }).format(date),
    dayOfMonth: (date) => new Intl.DateTimeFormat(tag, { day: 'numeric' }).format(date),
    dayLabel: (date) =>
      new Intl.DateTimeFormat(tag, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      }).format(date),
    // A date line with nothing beside it is better Gregorian than absent, so
    // this one still falls back. The range below it does not: its caller draws
    // the Gregorian days itself, and a second copy of them would be a line
    // claiming to be a second calendar.
    hijri: (date = new Date()) =>
      hijriText(DAY_AND_MONTH, (format) => format.format(date)) ??
      new Intl.DateTimeFormat(tag, DAY_AND_MONTH).format(date),
    hijriRange: (from, to) => hijriText(WHOLE_DATE, (format) => format.formatRange(from, to)),
    gregorianRange: (from, to) => new Intl.DateTimeFormat(tag, WHOLE_DATE).formatRange(from, to),
  };
}
